import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, LifeBuoy, Mail, Phone, Send, ShieldCheck, HelpCircle } from 'lucide-react';
import { http } from '@/lib/api';
import { toast } from 'sonner';

const SUPPORT_EMAIL = 'brotherhoodplusapp@gmail.com';

const TOPICS = [
  'Account & login',
  'Billing & subscription',
  'Technical issue',
  'Privacy & data',
  'Feedback',
  'Something else',
];

export default function SupportContact() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState(TOPICS[0]);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // ref string

  useEffect(() => {
    document.title = 'Support · Brotherhood+';
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'description'); document.head.appendChild(m); }
    m.setAttribute('content', 'Get help with Brotherhood+. Contact our support team, find crisis resources, and read our FAQs.');
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Please fill in your name, email, and message.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await http.post('/contact', {
        name: name.trim(),
        email: email.trim(),
        topic,
        message: message.trim(),
        page_url: window.location.pathname,
      });
      setDone(r.data?.ref || 'received');
      toast.success('Message sent. We will be in touch.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send your message. Please email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="support-contact-page">
      <div className="max-w-3xl mx-auto p-6 lg:p-10">
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-smooth" data-testid="support-back">
            <ArrowLeft size={14}/> Home
          </Link>
        </div>

        <header className="mb-10">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">
            <LifeBuoy size={13} strokeWidth={1.75}/> Support
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl mt-3 leading-tight text-navy">We&rsquo;re here to help.</h1>
          <p className="mt-4 text-base lg:text-lg text-navy/60 leading-relaxed max-w-2xl">
            Questions about your account, billing, privacy, or the app itself? Send us a message below and a member of the
            Brotherhood+ team will get back to you &mdash; usually within one business day.
          </p>
        </header>

        {/* Crisis banner — always visible */}
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5 mb-8 flex items-start gap-3" data-testid="support-crisis">
          <Phone size={18} className="text-red-700 mt-0.5 shrink-0"/>
          <p className="text-sm text-navy leading-relaxed">
            <span className="font-semibold text-red-800">In a crisis?</span> Brotherhood+ is not an emergency service.
            If you or someone you love is in immediate danger, call <span className="font-semibold">911</span>, or call/text
            <span className="font-semibold"> 988</span> (Suicide &amp; Crisis Lifeline, US &mdash; free &amp; 24/7).
          </p>
        </div>

        {/* Direct contact */}
        <div className="grid sm:grid-cols-3 gap-3 mb-10">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            data-testid="support-email-link"
            className="rounded-2xl bg-white border border-navy/5 p-5 hover:-translate-y-0.5 transition-smooth"
            style={{ boxShadow: '0 4px 20px rgba(11,25,44,0.04)' }}
          >
            <Mail size={18} className="text-gold"/>
            <p className="font-heading text-base text-navy mt-3">Email us</p>
            <p className="text-sm text-navy/55 mt-1 break-all">{SUPPORT_EMAIL}</p>
          </a>
          <Link
            to="/privacy"
            className="rounded-2xl bg-white border border-navy/5 p-5 hover:-translate-y-0.5 transition-smooth"
            style={{ boxShadow: '0 4px 20px rgba(11,25,44,0.04)' }}
          >
            <ShieldCheck size={18} className="text-gold"/>
            <p className="font-heading text-base text-navy mt-3">Privacy &amp; data</p>
            <p className="text-sm text-navy/55 mt-1">How we protect you.</p>
          </Link>
          <Link
            to="/terms"
            className="rounded-2xl bg-white border border-navy/5 p-5 hover:-translate-y-0.5 transition-smooth"
            style={{ boxShadow: '0 4px 20px rgba(11,25,44,0.04)' }}
          >
            <HelpCircle size={18} className="text-gold"/>
            <p className="font-heading text-base text-navy mt-3">Terms of Service</p>
            <p className="text-sm text-navy/55 mt-1">The rules of the road.</p>
          </Link>
        </div>

        {/* Contact form */}
        <div className="rounded-3xl bg-white border border-navy/5 p-6 lg:p-8" style={{ boxShadow: '0 8px 30px rgba(11,25,44,0.05)' }}>
          {done ? (
            <div className="text-center py-8" data-testid="support-thanks">
              <div className="w-14 h-14 mx-auto rounded-full bg-green/10 flex items-center justify-center mb-3">
                <Send className="text-green" size={24} strokeWidth={1.5}/>
              </div>
              <p className="font-heading text-2xl text-navy">Message received.</p>
              {done !== 'received' && (
                <p className="text-sm text-navy/70 mt-2">
                  Reference: <span className="font-mono font-semibold text-navy" data-testid="support-ref">{done}</span>
                </p>
              )}
              <p className="text-sm text-navy/60 mt-2 max-w-md mx-auto leading-relaxed">
                Thank you for reaching out. We&rsquo;ll reply to <span className="font-medium">{email}</span>, usually within one business day.
              </p>
              <button
                onClick={() => { setDone(null); setMessage(''); }}
                data-testid="support-send-another"
                className="mt-6 inline-flex items-center gap-2 bg-navy text-white rounded-full px-6 py-3 text-sm hover:bg-navy-soft transition-smooth"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="support-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-navy/50 font-semibold" htmlFor="sc-name">Your name</label>
                  <input
                    id="sc-name" data-testid="support-name"
                    value={name} onChange={(e) => setName(e.target.value)}
                    maxLength={120} placeholder="First and last"
                    className="mt-2 w-full bg-white border border-navy/10 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-navy/50 font-semibold" htmlFor="sc-email">Email</label>
                  <input
                    id="sc-email" data-testid="support-email" type="email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-2 w-full bg-white border border-navy/10 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-navy/50 font-semibold" htmlFor="sc-topic">Topic</label>
                <select
                  id="sc-topic" data-testid="support-topic"
                  value={topic} onChange={(e) => setTopic(e.target.value)}
                  className="mt-2 w-full bg-white border border-navy/10 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                >
                  {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-navy/50 font-semibold" htmlFor="sc-message">How can we help?</label>
                <textarea
                  id="sc-message" data-testid="support-message"
                  value={message} onChange={(e) => setMessage(e.target.value)}
                  rows={6} maxLength={5000} placeholder="Tell us what is going on..."
                  className="mt-2 w-full bg-white border border-navy/10 rounded-2xl p-4 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-gold resize-none"
                />
              </div>
              <button
                type="submit" data-testid="support-submit" disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white rounded-full py-3.5 text-sm font-medium hover:bg-navy-soft transition-smooth disabled:opacity-50"
              >
                <Send size={15}/>{submitting ? 'Sending...' : 'Send message'}
              </button>
              <p className="text-[11px] text-navy/45 text-center leading-relaxed">
                You can also email us any time at{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="underline hover:text-navy">{SUPPORT_EMAIL}</a>.
              </p>
            </form>
          )}
        </div>

        <footer className="mt-12 pt-6 border-t border-navy/10 text-sm text-navy/55">
          <p>Brotherhood+ · Rooted in culture. Made for every man ready to grow. · For men and the people who support them.</p>
          <p className="mt-1">
            <Link to="/privacy" className="underline hover:text-navy">Privacy Policy</Link>
            {' · '}
            <Link to="/terms" className="underline hover:text-navy">Terms of Service</Link>
          </p>
        </footer>
      </div>
    </div>
  );
}
